import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getTokenForRequest } from '../../utils/apiAuth';
import './UpdateEducatorsModal.css';

/**
 * Update Educators Modal Component
 * Handles bulk educator updates via CSV upload
 */
const UpdateEducatorsModal = ({ data, moduleSemesterId, onClose }) => {
  // Support both data prop (from ModuleAdmin) and direct moduleSemesterId prop
  const actualModuleSemesterId = moduleSemesterId || (data?.moduleSemester?.ID) || (data?.moduleSemesterId);
  const [file, setFile] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  // Handle file change
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setError(null);
    setMessage('');

    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv')) {
        processCSV(selectedFile);
      } else {
        setError('Only CSV files are allowed');
        setFile(null);
      }
    }
  };

  // Process CSV file
  const processCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let csvData = e.target.result;
        
        // Remove BOM (Byte Order Mark) if present
        if (csvData.charCodeAt(0) === 0xFEFF) {
          csvData = csvData.slice(1);
        }
        
        // Handle both \n and \r\n line endings
        const lines = csvData.split(/\r?\n/).filter(line => line.trim().length > 0);
        
        if (lines.length === 0) {
          setError('CSV file is empty!');
          setFile(null);
          return;
        }
        
        const headers = lines[0].split(',').map(h => h.trim());

        if (headers[0] !== 'EducatorEmail' || headers[1] !== 'ClassName') {
          setError(`Wrong format in selected CSV file! Expected headers: "EducatorEmail,ClassName" but found: "${headers[0]},${headers[1]}"`);
          setFile(null);
          return;
        }

        const processedRecords = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // Skip empty lines
          
          const values = line.split(',').map(v => v.trim());
          if (values[0] && values[1]) {
            processedRecords.push({
              Email: values[0],
              ClassName: values[1]
            });
          }
        }

        if (processedRecords.length === 0) {
          setError('No records found in the selected CSV file!');
          setFile(null);
        } else {
          setRecords(processedRecords);
          setMessage(`${processedRecords.length} records found. Click 'Update' to proceed.`);
        }
      } catch (err) {
        setError('Error processing CSV file');
        setFile(null);
      }
    };
    reader.readAsText(file);
  };

  // Encode token helper function
  const encodeToken = (token) => {
    if (window.encodeToken) {
      return window.encodeToken(token);
    }
    // Fallback encoding if window.encodeToken is not available
    return encodeURIComponent(token);
  };

  // Update educators
  const updateEducators = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getTokenForRequest();

      const response = await fetch('./webservices/Organization.svc/UpdateMsEducators', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: encodeToken(token),
          msId: actualModuleSemesterId,
          upeRecords: records
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        setError(`Failed to update educators: ${response.status} ${response.statusText}`);
        setLoading(false);
        return;
      }

      const result = await response.json();

      const classInfo = result.UpdatedClasses && result.UpdatedClasses.length === 0 
        ? 'No class is updated and '
        : result.UpdatedClasses && result.UpdatedClasses.length === 1
        ? '1 class is updated and '
        : result.UpdatedClasses
        ? `${result.UpdatedClasses.length} classes are updated and `
        : '';

      const recordInfo = result.RecordsInserted === 0
        ? 'No record is created.'
        : result.RecordsInserted === 1
        ? '1 record is created.'
        : `${result.RecordsInserted} records are created.`;

      alert(`Update is finished. ${classInfo}${recordInfo}`);
      onClose();
    } catch (err) {
      console.error('Error updating educators:', err);
      setError(`Error updating educators: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const modalContent = (
    <div className="update-educators-modal-overlay" onClick={onClose}>
      <div className="update-educators-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h5 className="modal-title">
            <i className="fas fa-users me-2"></i>
            Update Educators
          </h5>
          <button type="button" className="btn-close-x" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {message && (
            <div className="alert alert-info" role="alert">
              {message}
            </div>
          )}

          <div className="file-upload-section">
            <h6 className="section-title">Upload CSV File</h6>
            <p className="text-muted">
              Upload a CSV file with columns: EducatorEmail, ClassName
            </p>

            <div className="file-upload-controls">
              <div className="file-input-wrapper">
                <input
                  type="file"
                  className="file-input"
                  accept=".csv"
                  onChange={handleFileChange}
                  id="updateEducatorsFile"
                />
                <label 
                  htmlFor="updateEducatorsFile" 
                  className="btn btn-outline-secondary btn-sm file-choose-btn"
                >
                  <i className="fas fa-folder-open me-2"></i>
                  Choose File
                </label>
              </div>
            </div>

            {file && (
              <div className="file-info">
                <i className="fas fa-file-csv me-2"></i>
                {file.name}
              </div>
            )}
          </div>

          {records.length > 0 && (
            <div className="records-preview">
              <h6 className="section-title">Records Preview</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Class Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.slice(0, 5).map((record, index) => (
                      <tr key={index}>
                        <td>{record.Email}</td>
                        <td>{record.ClassName}</td>
                      </tr>
                    ))}
                    {records.length > 5 && (
                      <tr>
                        <td colSpan="2" className="text-muted">
                          ... and {records.length - 5} more records
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={updateEducators}
            disabled={loading || records.length === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Updating...
              </>
            ) : (
              'Update Educators'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal using Portal to document.body to ensure it's on top
  return createPortal(modalContent, document.body);
};

export default UpdateEducatorsModal; 